import React, { useEffect, useState } from 'react';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { Tabs, TabList, Tab, TabPanel } from 'react-tabs';
import { Button, Tooltip } from '@mui/material';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'react-tabs/style/react-tabs.css';
import './css/App.css';
import './css/style.css';
import './css/Card.css';

import Navbar from './components/Navbar';
import MarkIcon from './components/graphics/MarkIcon';
import WizardSearch from './components/common/wizards/WizardSearch';
import CreateData from './components/AddNewData';
import { ContextMessage } from './components/ContextMessage';
import { StorageProvider } from './App.Context';
import { getCurrentUserNameAndRoles } from './services/Cognito';
import { getDynamicCollectionsRegistry } from './api/DynamicCollections';
import Events from './events';
import { EventsType } from './events-types';

// Import TabPanelContents
import TabPanelContentPlantsVarieties from './tabs/TabPanelContent.plants_varieties';
import TabPanelContentCompounds from './tabs/TabPanelContent.compounds';
import TabPanelContentPharmacology from './tabs/TabPanelContent.pharmacology';
import TabPanelContentToxicity from './tabs/TabPanelContent.toxicity';
import TabPanelContentDisordersDiseases from './tabs/TabPanelContent.disorders_diceases';
import TabPanelContentQualitySafety from './tabs/TabPanelContent.quality_safety';
import TabPanelContentProducts from './tabs/TabPanelContent.products';
import TabPanelContentRegulations from './tabs/TabPanelContent.regulations';
import TabPanelContentPatents from './tabs/TabPanelContent.patents';
import TabPanelContentReferences from './tabs/TabPanelContent.references';
import TabPanelContentBaskets from './tabs/TabPanelContent.baskets';
import TabPanelContentLoggers from './tabs/TabPanelContent.loggers';

// Tabs configuration
import { TABS } from './tabs/TabsConfig';
import { AROMA, CANNABIS_COMPOUNDS, COMPOUND, MARKET_CBD,
    MUTAGENICITY, PHARMACO_DYNAMICS, PHYSICAL_CHARACTERIZATION_CBD, REFERENCES, STABILITY, TOXICANTS_DISEASE } from './api/Compounds';
import { STRAINS } from './api/Strains';
import {CBD_PK_INDICES, SC_PK, SC_THERAPEUTIC_INDICATIONS, STUDIES_METABOLITES } from './api/Studies';
import { PRODUCTS_ANALYSIS_METHODS, PRODUCTS_ANALYSIS_REPORTS, PRODUCT_ANALYSIS_VALUES, PRODUCT_DEGRADATION, PRODUCT_DEGRADATION_COMPOUNDS } from './api/Products';
import { Aroma, CannabisCompounds, Compound } from './components/tabs/compounds/types';
import { Reference } from './components/tabs/references/types';
import { Strain } from './components/tabs/plants_varieties/types';
import { TherapeuticIndicator } from './components/tabs/disorders_diseases/types';
import { Market_CBD, Physical_characterization_CBD, ProductAnalysisMethod, ProductAnalysisReport, ProductAnalysisValue } from './components/tabs/products/types';
import { Metabolite, PharmacoDynamics, PK, PK_Indices } from './components/tabs/pharmacology/types';
import {ProductDegradation, ProductDegradationCompounds, Stability} from "./components/tabs/quality_safety/types";
import {Mutagenicity, ToxicantsDisease} from "./components/tabs/toxicity/types";

function App() {
    const [roles, setRoles] = useState<string[]>([]);
    const [isReadOnly, setIsReadOnly] = useState(true);
    const [dynamicCollectionsRegistry, setDynamicCollectionsRegistry] = useState<any[]>([]);

    const listsOfObjects = {
        [`${AROMA}`]: Aroma,
        [`${CANNABIS_COMPOUNDS}`]: CannabisCompounds,
        [`${COMPOUND}`]: Compound,
        [`${REFERENCES}`]: Reference,
        [`${STRAINS}`]: Strain,
        [`${SC_THERAPEUTIC_INDICATIONS}`]: TherapeuticIndicator,
        [`${MARKET_CBD}`]: Market_CBD,
        [`${PHYSICAL_CHARACTERIZATION_CBD}`]: Physical_characterization_CBD,
        [`${STUDIES_METABOLITES}`]: Metabolite,
        [`${SC_PK}`]: PK,
        [`${CBD_PK_INDICES}`]: PK_Indices,
        [`${PHARMACO_DYNAMICS}`]: PharmacoDynamics,
        [`${PRODUCTS_ANALYSIS_METHODS}`]: ProductAnalysisMethod,
        [`${PRODUCT_ANALYSIS_VALUES}`]: ProductAnalysisValue,
        [`${PRODUCTS_ANALYSIS_REPORTS}`]: ProductAnalysisReport,
        [`${PRODUCT_DEGRADATION}`]: ProductDegradation,
        [`${PRODUCT_DEGRADATION_COMPOUNDS}`]: ProductDegradationCompounds,
        [`${MUTAGENICITY}`]: Mutagenicity,
        [`${STABILITY}`]: Stability,
        [`${TOXICANTS_DISEASE}`]: ToxicantsDisease,

    };

    const fetchCurrentUser = async () => {
        try {
            const { user, roles } = await getCurrentUserNameAndRoles();
            console.log('roles', roles);
            setRoles(roles);
            setIsReadOnly(roles.includes('ReadOnly'));
        } catch (error) {
            console.error('Error setting current user:', error);
        }
    };

    const fetchDynamicCollectionsRegistry = async () => {
        try {
            const registry = await getDynamicCollectionsRegistry();
            setDynamicCollectionsRegistry(registry);
        } catch (error) {
            console.error('Failed to fetch dynamic collections registry', error);
        }
    };

    useEffect(() => {
        fetchCurrentUser();
        fetchDynamicCollectionsRegistry();
    }, []);

    const clearAll = () => {
        Events.trigger(EventsType.CLEAR_ALL_DATA, []);
    };

    return (
        <StorageProvider>
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-gb">
                <div className="App">
                    <Navbar />
                    <ContextMessage>
                        <Tabs forceRenderTabPanel={true}>
                            <TabList style={{ textAlign: 'left' }}>
                                {TABS.map(tab => (
                                    <Tab key={tab.name}>
                                        <span style={{ display: 'flex', alignItems: 'center' }}>
                                          {tab.label}
                                            <MarkIcon width="10px" name={tab.name} />
                                        </span>
                                    </Tab>
                                ))}
                            </TabList>

                            {/* TAB PANELS */}
                            <TabPanel forceRender={true}>
                                <TabPanelContentPlantsVarieties dynamicCollections={dynamicCollectionsRegistry} />
                            </TabPanel>

                            <TabPanel forceRender={true}>
                                <TabPanelContentCompounds dynamicCollections={dynamicCollectionsRegistry} />
                            </TabPanel>

                            <TabPanel forceRender={true}>
                                <TabPanelContentPharmacology dynamicCollections={dynamicCollectionsRegistry} />
                            </TabPanel>

                            <TabPanel forceRender={true}>
                                <TabPanelContentToxicity dynamicCollections={dynamicCollectionsRegistry} />
                            </TabPanel>

                            <TabPanel forceRender={true}>
                                <TabPanelContentDisordersDiseases dynamicCollections={dynamicCollectionsRegistry} />
                            </TabPanel>

                            <TabPanel forceRender={true}>
                                <TabPanelContentQualitySafety dynamicCollections={dynamicCollectionsRegistry} />
                            </TabPanel>

                            <TabPanel forceRender={true}>
                                <TabPanelContentProducts dynamicCollections={dynamicCollectionsRegistry} />
                            </TabPanel>

                            <TabPanel forceRender={true}>
                                <TabPanelContentRegulations dynamicCollections={dynamicCollectionsRegistry} />
                            </TabPanel>

                            <TabPanel forceRender={true}>
                                <TabPanelContentPatents dynamicCollections={dynamicCollectionsRegistry} />
                            </TabPanel>

                            <TabPanel forceRender={true}>
                                <TabPanelContentReferences dynamicCollections={dynamicCollectionsRegistry} />
                            </TabPanel>

                            <TabPanel forceRender={true}>
                                <TabPanelContentBaskets  />
                            </TabPanel>

                            <TabPanel forceRender={true}>
                                <TabPanelContentLoggers  isReadOnly={isReadOnly} />
                            </TabPanel>
                        </Tabs>

                        <div className="buttonOnTabs">
                            <WizardSearch data={listsOfObjects} /> {/* Adjust if needed */}
                            {!isReadOnly && <CreateData listsOfObjects={listsOfObjects}  />}
                            <Tooltip title="Reset All data views" placement="top" arrow>
                                <Button
                                    style={{ height: '32px', boxShadow: '0px 0px 0px 0px' }}
                                    variant="contained"
                                    color="primary"
                                    onClick={clearAll}
                                    aria-label="Reset All data views"
                                >
                                    Reset
                                </Button>
                            </Tooltip>
                        </div>
                    </ContextMessage>
                </div>
            </LocalizationProvider>
        </StorageProvider>
    );
}

export default App;
